import React from 'react';

import { useGetAssociationAffiliatedMembers } from 'legacy/groups/hooks';
import Loader from 'legacy/common/components/layout/Loader';
import { Avatar } from 'components/Avatar';
import MemberList from 'components/lists/MemberList';
import MembersListItem from 'components/lists/MemberList/ListItem';
import { useTranslation } from 'react-i18next';
import DownloadCSVButton from 'components/DownloadCSVButton';
import { saveAs } from 'file-saver';
import { toast } from 'react-hot-toast';
import { useExportAssociationAffiliatesCSV } from 'api/queries/associations';

interface Props {
  associationId: string;
  search: string;
}

const AffiliatesList = ({ associationId, search }: Props) => {
  const { t } = useTranslation(['associations']);
  const {
    isLoading,
    data,
    fetchNextPage,
    fetchPreviousPage,
    hasPreviousPage,
    hasNextPage
  } = useGetAssociationAffiliatedMembers(associationId, search);

  const { isLoading: isExporting, refetch: exportCSV } =
    useExportAssociationAffiliatesCSV({
      associationId,
      enabled: false
    });

  const onClickExportCSV = async () => {
    try {
      const result = await exportCSV();
      if (result.data) {
        const today = new Date().toISOString().split('T')[0]; // yyyy-mm-dd format
        saveAs(result.data, `association-affiliate-members-${today}.csv`);
      }
    } catch (error) {
      toast.error(t('members.exportAffiliatedMembersFailed'));
    }
  };

  return (
    <MemberList
      title={t('members.affiliates')}
      description={t('members.affiliatesDescription')}
      onNext={fetchNextPage}
      onPrev={fetchPreviousPage}
      hasNextPage={hasNextPage}
      hasPreviousPage={hasPreviousPage}
      headerContent={
        <div className="flex justify-start">
          <DownloadCSVButton onClick={onClickExportCSV} loading={isExporting} />
        </div>
      }
    >
      {isLoading ? (
        <Loader isLoading />
      ) : (
        data?.map((member) => (
          <MembersListItem
            key={member.user.id}
            avatar={<Avatar src={member.user.photoUrl} />}
            label={`${member.user.firstName} ${member.user.lastName}`}
          />
        ))
      )}
    </MemberList>
  );
};

export default AffiliatesList;
